import React, { useCallback, useEffect, useState } from 'react';
import { Topbar, CalendarModal } from '@components';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
    Select,
    Spin,
} from 'antd';
import { CalendarFilled } from '@assets';
import { PRIVATE_ROUTE } from '@router/route.constants';
import { api } from '@api';
import {
    LIMITS,
    TOPUP_SK,
    WITHDRAW_SK,
    BONUS_SK,
} from '@constants';
import InfiniteScroll from 'react-infinite-scroll-component';
import { getFormattedNumber } from '@utils';
import moment from 'moment';
import { BonusImg } from '../Wallet/Wallet';
import { useTranslation } from 'react-i18next';
import { getStatus } from '@utils';
import {
    AmountText,
    DateText,
    ExpiredLabel,
    ExpiredText,
    ItemImage,
    ItemText,
    ListContainer,
    ListItem,
    StatusText,
    Wrapper,
    EmptyText,
    BonusStatusText,
    DataWrapper,
} from '../Wallet/Wallet.style';
import {
    ContainerOutlined,
    CaretDownOutlined,
} from '@ant-design/icons';

const RootContainer = styled.div`
padding: 16px 0;
height: calc(100vh - 102px);
overflow: hidden;

.empty-box {
    height: calc(100vh - 140px);
    display: flex;
    flex-direction: column;
    gap: 6px;
    align-items: center;
    justify-content: center;

    svg {
        font-size: 24px;
        color: #808080;
    }
}

.anticon .anticon-calendar {
    font-size: 28px;
    color: #fff;
}
`;

const Top = styled.div`
margin: 0 16px 16px 16px;

    .ant-select {
        width: 100%;

        .ant-select-selector{
            border: none;
            background-color: #EFEFEF;
        }

        .ant-select-selection-item {
            font-size: 14px;
            font-style: normal;
            font-weight: 700;
            line-height: normal;
            color: #101415;
        }

        .ant-select-arrow svg {
            font-size: 18px;
            color: #C21C05;
        }
    }
`;

const StyledListWrapper = styled(ListContainer).attrs({
    id: 'scrollable-div',
})`
height: calc(100vh - 140px);
padding-top: 0 !important;
`;

const SpinWrapper = styled.div`
text-align: center;
`;

const {
    DEFAULT_PAGE,
    PAGE_SIZE,
} = LIMITS;

const dateFormat = 'MMM DD, YYYY hh:mm:ss A';
const apiDateFormat = 'YYYY-MM-DD';

const TransactionHistory = () => {
    const { t } = useTranslation()
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [type, setType] = useState('');
    const [page, setPage] = useState(DEFAULT_PAGE);
    const [total, setTotal] = useState(0);
    const [open, setOpen] = useState(false);
    const [date, setDate] = useState('');
    const [loading, setLoading] = useState(true);

    const getTransactions = useCallback(() => {
        setLoading(true);
        api
            .getTransactions({
                page,
                limit: PAGE_SIZE,
                type,
                start_date: date,
                end_date: date,
            })
            .then(({ transactions, pagination }) => {
                setData(prevState => {
                    return [
                        ...prevState,
                        ...transactions,
                    ];
                });
                setPage(pagination.page);
                setTotal(pagination.total);
                setLoading(false);
            })
            .catch(() => { });

    }, [type, page, date]);

    useEffect(() => {
        getTransactions();

    }, [getTransactions]);

    const onChange = (value) => {
        setType(value);
        setPage(DEFAULT_PAGE);
        setData([]);
        setTotal(0);
    };

    const onOk = (newdate) => {
        newdate = newdate.format(apiDateFormat);
        if (newdate !== date) {
            setDate(newdate);
            setType('');
            setPage(DEFAULT_PAGE);
            setData([]);
            setTotal(0);
        }
        handleClose();
    }

    const onReset = () => {
        if (date) {
            setDate('');
            setType('');
            setPage(DEFAULT_PAGE);
            setData([]);
            setTotal(0);
        }
    }

    const handleClose = () => {
        setOpen(false);
    }

    const goBack = () => {
        navigate(`/${PRIVATE_ROUTE.WALLET}`);
    }

    const options = [
        {
            value: '',
            label: t("wallet.transactionHistoryHeadingAll"),
        },
        {
            value: WITHDRAW_SK,
            label: t("wallet.walletWithdraw"),
        },
        {
            value: TOPUP_SK,
            label: t("wallet.walletTopUp"),
        },
        {
            value: BONUS_SK,
            label: t("wallet.transactionHistoryHeadingBonus"),
        },
    ]

    const renderTop = () => (
        <Top>
            <Select
                size='large'
                value={type}
                options={options}
                onChange={onChange}
                popupClassName='trx-history-dropdown'
                suffixIcon={<CaretDownOutlined />}
            />
        </Top>
    );

    const Loader = () => (
        <SpinWrapper>
            <Spin />
        </SpinWrapper>
    );

    const EmptyCard = () => (
        <div className="empty-box">
            <ContainerOutlined />
            <EmptyText>
                {t("common.noRecord")}
            </EmptyText>
        </div>
    );

    return (
        <>
            <Topbar
                title={t("wallet.transactionHistoryTitle")}
                rightIcon={
                    <CalendarFilled onClick={() => setOpen(true)} />
                }
                goBack={goBack}
            />
            <RootContainer>
                {renderTop()}
                <StyledListWrapper>
                    <InfiniteScroll
                        dataLength={data.length}
                        next={() => setPage((prev) => prev + 1)}
                        hasMore={loading || data.length < total}
                        loader={<Loader />}
                        scrollableTarget='scrollable-div'
                        endMessage={!loading && data.length === 0 && <EmptyCard />}
                    >
                        {data.map((item, key) => (
                            <ListItem key={key}>
                                {item.type === BONUS_SK
                                    ? <BonusImg type={item.bonus_type} />
                                    : <ItemImage src={item.payment_category_image} />
                                }
                                <DataWrapper>
              <Wrapper>
                <ItemText>
                  {item.type === BONUS_SK
                    ? t("wallet.transactionHistoryBonusType", {
                      bonus_type: item.bonus_type.replaceAll('-bonus',''),
                    })
                    : `${item.type === WITHDRAW_SK
                      ? t("wallet.walletWithdraw")
                      : t("wallet.walletTopUp")
                    } ${t("common.to")} ${item.num}`}
                </ItemText>
                <AmountText>
                  {`${getFormattedNumber(item.amount)} Ks.`}
                </AmountText>
              </Wrapper>
              <Wrapper>
                <DateText>
                  {moment.unix(item.created_at).format(dateFormat)}
                </DateText>
                {item.type === BONUS_SK && item.bonus_expired && (
                  <BonusStatusText status={item.status}>
                    {item.status === 'Used'
                      ? t("wallet.bonusUsedText")
                      : t("wallet.bonusNotUsedText")
                    }
                  </BonusStatusText>
                )}
                {item.type !== BONUS_SK && (
                  <StatusText status={item.status}>
                    {getStatus(item.status)}
                  </StatusText>
                )}
              </Wrapper>
              <Wrapper>
                {item.trx_num &&
                  <DateText>
                    {`PIN: ${item.trx_num}`}
                  </DateText>
                }
                {item.type === BONUS_SK && item.bonus_expired &&
                  <div style={{ display: "flex" }}>
                    <ExpiredLabel>{t("wallet.expiredLabel")}</ExpiredLabel>
                    <ExpiredText>&nbsp;&nbsp;
                      {moment.unix(item.bonus_expired).format(dateFormat)}
                    </ExpiredText>
                  </div>
                }
                
              </Wrapper>
            </DataWrapper>
                            </ListItem>
                        ))}
                    </InfiniteScroll>
                </StyledListWrapper>
            </RootContainer>
            <CalendarModal
                open={open}
                date={date}
                onOk={onOk}
                onReset={onReset}
                handleClose={handleClose}
            />
        </>
    );
}

export default TransactionHistory;